import type { AppRouteRecordRaw } from '@/router/types'
import { Layout } from '@/utils/routerHelper'

/**
 * 问卷调查独立路由组
 * 正式前缀：/questionnaire/*（独立窗 app=questionnaire）
 * 侧栏业务菜单仍来自后端 /survey/*（见 menuPaths）
 */
const questionnaireStandaloneRoutes: AppRouteRecordRaw[] = [
  {
    path: '/questionnaire',
    component: Layout,
    // 入口与侧栏「概况」一致，避免进入 dashboard 后侧栏找不到当前页
    redirect: '/questionnaire/overview',
    name: 'QuestionnaireStandalone',
    meta: { title: '问卷调查', hidden: true },
    children: [
      {
        path: 'dashboard',
        redirect: '/questionnaire/overview',
        name: 'QuestionnaireStandaloneDashboard',
        meta: { title: '问卷概况', hidden: true, canTo: true }
      },
      {
        path: 'overview',
        component: () => import('@/views/survey/overview/index.vue'),
        name: 'QuestionnaireStandaloneOverview',
        meta: { title: '问卷概况', hidden: true, canTo: true }
      },
      {
        path: 'overview/dashboard',
        redirect: '/questionnaire/overview',
        name: 'QuestionnaireStandaloneLegacyDashboardRedirect',
        meta: { hidden: true, canTo: true }
      },
      {
        path: 'decoration',
        component: () => import('@/views/survey/decoration/index.vue'),
        name: 'QuestionnaireStandaloneDecoration',
        meta: { hidden: true, canTo: true, title: '页面装修' }
      },
      {
        path: 'design/manage',
        component: () => import('@/views/survey/manage/index.vue'),
        name: 'QuestionnaireStandaloneDesignManage',
        meta: {
          hidden: true,
          canTo: true,
          title: '问卷管理',
          activeMenu: '/questionnaire/design/manage'
        }
      },
      {
        path: 'editor',
        component: () => import('@/views/survey/editor/index.vue'),
        name: 'QuestionnaireStandaloneEditor',
        meta: {
          hidden: true,
          canTo: true,
          title: '问卷编辑器',
          activeMenu: '/questionnaire/create'
        }
      },
      {
        path: 'create',
        component: () => import('@/views/survey/create/index.vue'),
        name: 'QuestionnaireStandaloneCreate',
        meta: { hidden: true, canTo: true, title: '创建问卷' }
      },
      {
        path: 'templates',
        component: () => import('@/views/survey/templates/index.vue'),
        name: 'QuestionnaireStandaloneTemplates',
        meta: {
          hidden: true,
          canTo: true,
          title: '问卷模板',
          activeMenu: '/questionnaire/create'
        }
      },
      {
        path: 'publish',
        component: () => import('@/views/survey/publish/index.vue'),
        name: 'QuestionnaireStandalonePublish',
        meta: {
          hidden: true,
          canTo: true,
          title: '投放与回收',
          activeMenu: '/questionnaire/publish'
        }
      },
      {
        path: 'import',
        component: () => import('@/views/survey/import/index.vue'),
        name: 'QuestionnaireStandaloneImport',
        meta: {
          hidden: true,
          canTo: true,
          title: '导入问卷',
          activeMenu: '/questionnaire/create'
        }
      },
      {
        path: 'access',
        component: () => import('@/views/survey/access/index.vue'),
        name: 'QuestionnaireStandaloneAccess',
        meta: { hidden: true, canTo: true, title: '访问规则' }
      },
      {
        path: 'behavior',
        component: () => import('@/views/survey/behavior/index.vue'),
        name: 'QuestionnaireStandaloneBehavior',
        meta: { hidden: true, canTo: true, title: '作答行为' }
      },
      {
        path: 'incentive',
        component: () => import('@/views/survey/incentive/index.vue'),
        name: 'QuestionnaireStandaloneIncentive',
        meta: { hidden: true, canTo: true, title: '激励配置' }
      },
      {
        path: 'response',
        component: () => import('@/views/survey/response/index.vue'),
        name: 'QuestionnaireStandaloneResponse',
        meta: { hidden: true, canTo: true, title: '答卷管理' }
      },
      {
        path: 'analysis/overview',
        component: () => import('@/views/survey/analysis/overview/index.vue'),
        name: 'QuestionnaireStandaloneAnalysisOverview',
        meta: { hidden: true, canTo: true, title: '数据分析' }
      },
      {
        path: 'analysis/cross',
        component: () => import('@/views/survey/analysis/cross/index.vue'),
        name: 'QuestionnaireStandaloneAnalysisCross',
        meta: { hidden: true, canTo: true, title: '交叉分析' }
      },
      {
        path: 'analysis/text',
        component: () => import('@/views/survey/analysis/text/index.vue'),
        name: 'QuestionnaireStandaloneAnalysisText',
        meta: { hidden: true, canTo: true, title: '文本分析' }
      },
      {
        path: 'analysis/report',
        component: () => import('@/views/survey/analysis/report/index.vue'),
        name: 'QuestionnaireStandaloneAnalysisReport',
        meta: { hidden: true, canTo: true, title: '多维报表' }
      },
      {
        path: 'sso',
        component: () => import('@/views/survey/sso/index.vue'),
        name: 'QuestionnaireStandaloneSso',
        meta: { hidden: true, canTo: true, title: 'SSO 登录' }
      },
      {
        path: 'collaborator',
        component: () => import('@/views/survey/collaborator/index.vue'),
        name: 'QuestionnaireStandaloneCollaborator',
        meta: { hidden: true, canTo: true, title: '协作子账号' }
      },
      {
        path: 'publish-audit',
        component: () => import('@/views/survey/publish-audit/index.vue'),
        name: 'QuestionnaireStandalonePublishAudit',
        meta: { hidden: true, canTo: true, title: '发布审批' }
      },
      {
        path: 'ai/insight',
        component: () => import('@/views/survey/ai/insight/index.vue'),
        name: 'QuestionnaireStandaloneAiInsight',
        meta: { hidden: true, canTo: true, title: 'AI 洞察' }
      },
      {
        path: 'exam',
        component: () => import('@/views/survey/exam/index.vue'),
        name: 'QuestionnaireStandaloneExam',
        meta: { hidden: true, canTo: true, title: '在线考试' }
      },
      {
        path: 'export',
        component: () => import('@/views/survey/export/index.vue'),
        name: 'QuestionnaireStandaloneExport',
        meta: { hidden: true, canTo: true, title: '数据导出' }
      },
      {
        path: 'reward',
        component: () => import('@/views/survey/reward/index.vue'),
        name: 'QuestionnaireStandaloneReward',
        meta: { hidden: true, canTo: true, title: '奖励发放' }
      },
      {
        path: 'signup',
        component: () => import('@/views/survey/signup/index.vue'),
        name: 'QuestionnaireStandaloneSignup',
        meta: { hidden: true, canTo: true, title: '报名签到' }
      },
      {
        path: 'vote',
        component: () => import('@/views/survey/vote/index.vue'),
        name: 'QuestionnaireStandaloneVote',
        meta: { hidden: true, canTo: true, title: '投票评选' }
      },
      {
        path: 'scenario',
        component: () => import('@/views/survey/scenario/index.vue'),
        name: 'QuestionnaireStandaloneScenario',
        meta: { hidden: true, canTo: true, title: '应用场景' }
      },
      {
        path: 'template-manage',
        component: () => import('@/views/survey/template-manage/index.vue'),
        name: 'QuestionnaireStandaloneTemplateManage',
        meta: { hidden: true, canTo: true, title: '模板管理' }
      },
      {
        path: 'banner',
        component: () => import('@/views/survey/banner/index.vue'),
        name: 'QuestionnaireStandaloneBanner',
        meta: { hidden: true, canTo: true, title: '轮播图管理' }
      },
      {
        path: 'client-home',
        component: () => import('@/views/survey/client-home/index.vue'),
        name: 'QuestionnaireStandaloneClientHome',
        meta: { hidden: true, canTo: true, title: 'C端首页配置' }
      },
      {
        path: 'member',
        component: () => import('@/views/survey/member/index.vue'),
        name: 'QuestionnaireStandaloneMember',
        meta: { hidden: true, canTo: true, title: '会员管理' }
      },
      {
        path: 'user',
        component: () => import('@/views/survey/user/index.vue'),
        name: 'QuestionnaireStandaloneUser',
        meta: { hidden: true, canTo: true, title: '用户管理' }
      },
      {
        path: 'user-system/feedback',
        component: () => import('@/views/survey/feedback/index.vue'),
        name: 'QuestionnaireStandaloneFeedback',
        meta: { hidden: true, canTo: true, title: '意见反馈' }
      },
      {
        path: 'cockpit',
        component: () => import('@/views/survey/cockpit/index.vue'),
        name: 'QuestionnaireStandaloneCockpit',
        meta: { hidden: true, canTo: true, title: '运营驾驶舱' }
      },
      {
        path: 'design/create',
        component: () => import('@/views/survey/create/index.vue'),
        name: 'QuestionnaireStandaloneDesignCreate',
        meta: { hidden: true, canTo: true, title: '创建问卷' }
      },
      {
        path: 'design/templates',
        component: () => import('@/views/survey/templates/index.vue'),
        name: 'QuestionnaireStandaloneDesignTemplates',
        meta: { hidden: true, canTo: true, title: '模板中心' }
      },
      {
        path: 'design/import',
        component: () => import('@/views/survey/import/index.vue'),
        name: 'QuestionnaireStandaloneDesignImport',
        meta: { hidden: true, canTo: true, title: '文档导入' }
      },
      {
        path: 'design/editor',
        component: () => import('@/views/survey/editor/index.vue'),
        name: 'QuestionnaireStandaloneDesignEditor',
        meta: { hidden: true, canTo: true, title: '问卷编辑器' }
      },
      {
        path: 'design/template-manage',
        component: () => import('@/views/survey/template-manage/index.vue'),
        name: 'QuestionnaireStandaloneDesignTemplateManage',
        meta: { hidden: true, canTo: true, title: '模板管理' }
      },
      {
        path: 'analysis/response',
        component: () => import('@/views/survey/response/index.vue'),
        name: 'QuestionnaireStandaloneAnalysisResponse',
        meta: { hidden: true, canTo: true, title: '答卷管理' }
      },
      {
        path: 'analysis/export',
        component: () => import('@/views/survey/export/index.vue'),
        name: 'QuestionnaireStandaloneAnalysisExport',
        meta: { hidden: true, canTo: true, title: '数据导出' }
      },
      {
        path: 'scenario/core',
        component: () => import('@/views/survey/scenario/index.vue'),
        name: 'QuestionnaireStandaloneScenarioCore',
        meta: { hidden: true, canTo: true, title: '六大核心场景' }
      },
      {
        path: 'publish-access/publish',
        component: () => import('@/views/survey/publish/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessPublish',
        meta: { hidden: true, canTo: true, title: '发布状态' }
      },
      {
        path: 'publish-access/access',
        component: () => import('@/views/survey/access/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessAccess',
        meta: { hidden: true, canTo: true, title: '访问控制' }
      },
      {
        path: 'publish-access/behavior',
        component: () => import('@/views/survey/behavior/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessBehavior',
        meta: { hidden: true, canTo: true, title: '作答行为' }
      },
      {
        path: 'publish-access/incentive',
        component: () => import('@/views/survey/incentive/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessIncentive',
        meta: { hidden: true, canTo: true, title: '激励机制' }
      },
      {
        path: 'publish-access/banner',
        component: () => import('@/views/survey/banner/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessBanner',
        meta: { hidden: true, canTo: true, title: '首页横幅' }
      },
      {
        path: 'publish-access/client-home',
        component: () => import('@/views/survey/client-home/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessClientHome',
        meta: { hidden: true, canTo: true, title: 'C端首页配置' }
      },
      {
        path: 'publish-access/exam',
        component: () => import('@/views/survey/exam/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessExam',
        meta: { hidden: true, canTo: true, title: '考试管理' }
      },
      {
        path: 'publish-access/signup',
        component: () => import('@/views/survey/signup/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessSignup',
        meta: { hidden: true, canTo: true, title: '报名管理' }
      },
      {
        path: 'publish-access/vote',
        component: () => import('@/views/survey/vote/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessVote',
        meta: { hidden: true, canTo: true, title: '投票管理' }
      },
      {
        path: 'publish-access/evaluation360',
        component: () => import('@/views/survey/evaluation360/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessEvaluation360',
        meta: { hidden: true, canTo: true, title: '评估管理' }
      },
      {
        path: 'publish-access/reward',
        component: () => import('@/views/survey/reward/index.vue'),
        name: 'QuestionnaireStandalonePublishAccessReward',
        meta: { hidden: true, canTo: true, title: '激励发放' }
      },
      {
        path: 'operation',
        component: () => import('@/views/survey/overview/index.vue'),
        name: 'QuestionnaireStandaloneOperation',
        meta: { hidden: true, canTo: true, title: '运营概况' }
      },
      {
        path: 'members/user',
        component: () => import('@/views/survey/user/index.vue'),
        name: 'QuestionnaireStandaloneMembersUser',
        meta: { hidden: true, canTo: true, title: '用户管理' }
      },
      {
        path: 'members/feedback',
        component: () => import('@/views/survey/feedback/index.vue'),
        name: 'QuestionnaireStandaloneMembersFeedback',
        meta: { hidden: true, canTo: true, title: '意见反馈' }
      },
      {
        path: 'members/member',
        component: () => import('@/views/survey/member/index.vue'),
        name: 'QuestionnaireStandaloneMembersMember',
        meta: { hidden: true, canTo: true, title: '会员管理' }
      },
      {
        path: 'config',
        component: () => import('@/views/system/dict/ModuleDict.vue'),
        name: 'QuestionnaireStandaloneConfig',
        meta: { hidden: true, canTo: true, title: '问卷字典管理', module: 'survey' }
      },
      {
        path: 'ops/tag',
        component: () => import('@/views/mp/tag/index.vue'),
        name: 'QuestionnaireStandaloneOpsTag',
        meta: { hidden: true, canTo: true, title: '标签管理' }
      }
    ]
  }
]

export default questionnaireStandaloneRoutes
